import React, { useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import { updatePassword, updateEmail, sendVerificationEmail } from './firebase';
import { authenticatedApi, getAuthAwareErrorMessage } from '../lib/authenticatedApi';
import './auth.css';

export default function Settings({ onClose }) {
  const { user, role, logout } = useAuth();
  const [tab, setTab] = useState('account');
  const isAdmin = role === 'admin';

  // Password form
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwMsg, setPwMsg] = useState({ type: '', text: '' });
  const [pwLoading, setPwLoading] = useState(false);

  // Email form
  const [newEmail, setNewEmail] = useState('');
  const [emailCurrentPw, setEmailCurrentPw] = useState('');
  const [emailMsg, setEmailMsg] = useState({ type: '', text: '' });
  const [emailLoading, setEmailLoading] = useState(false);

  // Verify email
  const [verifyMsg, setVerifyMsg] = useState({ type: '', text: '' });
  const [internalSettings, setInternalSettings] = useState(null);
  const [settingsMsg, setSettingsMsg] = useState('');
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [invites, setInvites] = useState([]);
  const [auditRows, setAuditRows] = useState([]);
  const [secureInviteEmail, setSecureInviteEmail] = useState('');
  const [secureInviteRole, setSecureInviteRole] = useState('tech_support');
  const [secureInviteTtl, setSecureInviteTtl] = useState(168);
  const [secureInviteMaxUses, setSecureInviteMaxUses] = useState(1);
  const [secureInvitePackage, setSecureInvitePackage] = useState(null);
  const [timeDraft, setTimeDraft] = useState({ default_timezone: 'UTC', display_location: 'global', district_local_time_enabled: false });
  const [calendarDraft, setCalendarDraft] = useState({ district_calendar_enabled: false, calendar_refresh_minutes: 30, offline_cache_hours: 48 });

  const internalTabs = useMemo(() => ([
    { key: 'access-control', label: 'Access Control' },
    { key: 'integrations', label: 'Integrations / API Keys' },
    { key: 'time-location', label: 'Time & Location' },
    { key: 'map-weather', label: 'Map & Weather Layers' },
    { key: 'bus-signing', label: 'Bus / Signing / Observability' },
    { key: 'runtime-show', label: 'Runtime / Show' },
    { key: 'sensor-controls', label: 'Sensor Controls' },
    { key: 'calendar-district', label: 'Calendar / District Time' },
  ]), []);

  const sectionKeyByTab = useMemo(() => ({
    integrations: 'integrations_api_keys',
    'map-weather': 'map_weather_layers',
    'bus-signing': 'bus_signing_observability',
    'runtime-show': 'runtime_show',
    'sensor-controls': 'sensor_controls',
  }), []);

  const submitPassword = async (e) => {
    e.preventDefault();
    setPwMsg({ type: '', text: '' });
    if (newPassword.length < 8) return setPwMsg({ type: 'err', text: 'Password must be at least 8 characters.' });
    if (newPassword !== confirmPassword) return setPwMsg({ type: 'err', text: 'Passwords do not match.' });
    setPwLoading(true);
    try {
      await updatePassword(currentPassword, newPassword);
      setPwMsg({ type: 'ok', text: 'Password updated successfully.' });
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
    } catch (err) {
      setPwMsg({ type: 'err', text: err?.message || 'Update failed.' });
    } finally {
      setPwLoading(false);
    }
  };

  const submitEmail = async (e) => {
    e.preventDefault();
    setEmailMsg({ type: '', text: '' });
    setEmailLoading(true);
    try {
      await updateEmail(emailCurrentPw, newEmail.trim());
      setEmailMsg({ type: 'ok', text: 'Email updated. A verification email has been sent to the new address.' });
      // Fire verification
      try { await sendVerificationEmail(); } catch (e) {}
      setEmailCurrentPw(''); setNewEmail('');
    } catch (err) {
      setEmailMsg({ type: 'err', text: err?.message || 'Email update failed.' });
    } finally {
      setEmailLoading(false);
    }
  };

  const sendVerify = async () => {
    setVerifyMsg({ type: '', text: '' });
    try {
      await sendVerificationEmail();
      setVerifyMsg({ type: 'ok', text: 'Verification email sent. Check your inbox.' });
    } catch (err) {
      setVerifyMsg({ type: 'err', text: err?.message || 'Failed to send verification.' });
    }
  };

  const loadInternal = async () => {
    setSettingsLoading(true);
    setSettingsMsg('');
    try {
      const [settingsRes, adminsRes, invitesRes, auditRes, timeRes, calRes] = await Promise.all([
        authenticatedApi.get('/api/internal/settings'),
        authenticatedApi.get('/api/internal/auth-admin/admins'),
        authenticatedApi.get('/api/internal/auth-admin/invites'),
        authenticatedApi.get('/api/internal/auth-admin/audit'),
        authenticatedApi.get('/api/internal/time/settings'),
        authenticatedApi.get('/api/internal/calendar/state'),
      ]);
      const cfg = settingsRes.data || {};
      const timeCfg = timeRes.data || {};
      const calCfg = (calRes.data && calRes.data.calendar) || {};
      setInternalSettings(cfg);
      setAdmins(adminsRes.data || []);
      setInvites(invitesRes.data || []);
      setAuditRows(auditRes.data || []);
      setTimeDraft({
        default_timezone: timeCfg.default_timezone || 'UTC',
        display_location: timeCfg.display_location || 'global',
        district_local_time_enabled: !!timeCfg.district_local_time_enabled,
      });
      setCalendarDraft({
        district_calendar_enabled: !!calCfg.district_calendar_enabled,
        calendar_refresh_minutes: Number(calCfg.calendar_refresh_minutes || 30),
        offline_cache_hours: Number(calCfg.offline_cache_hours || 48),
      });
    } catch (err) {
      setSettingsMsg(getAuthAwareErrorMessage(err, 'Failed to load internal settings'));
    } finally {
      setSettingsLoading(false);
    }
  };

  const openInternalTab = async (key) => {
    setTab(key);
    if (!internalSettings) {
      await loadInternal();
    }
  };

  const saveTimeLocation = async () => {
    setSettingsLoading(true);
    setSettingsMsg('');
    try {
      await authenticatedApi.patch('/api/internal/time/settings', timeDraft);
      await authenticatedApi.patch('/api/internal/calendar/state', calendarDraft);
      setSettingsMsg('Time and calendar settings updated.');
    } catch (err) {
      setSettingsMsg(getAuthAwareErrorMessage(err, 'Failed to update time/calendar settings'));
    } finally {
      setSettingsLoading(false);
    }
  };

  const setAdminRole = async (uid, nextRole) => {
    if (!isAdmin) return;
    try {
      await authenticatedApi.post('/api/internal/auth-admin/admins/role', { uid, role: nextRole });
      await loadInternal();
    } catch (err) {
      setSettingsMsg(getAuthAwareErrorMessage(err, 'Failed to update role'));
    }
  };

  const setAdminStatus = async (uid, disabled) => {
    if (!isAdmin) return;
    try {
      await authenticatedApi.post('/api/internal/auth-admin/admins/status', { uid, disabled });
      await loadInternal();
    } catch (err) {
      setSettingsMsg(getAuthAwareErrorMessage(err, 'Failed to update status'));
    }
  };

  const forceReauth = async (uid) => {
    if (!isAdmin) return;
    try {
      await authenticatedApi.post(`/api/internal/auth-admin/admins/force-reauth/${uid}`);
      setSettingsMsg('Forced re-auth issued.');
    } catch (err) {
      setSettingsMsg(getAuthAwareErrorMessage(err, 'Failed to force re-auth'));
    }
  };

  const createSecureInvitePackage = async (e) => {
    e.preventDefault();
    if (!isAdmin) return;
    setSettingsLoading(true);
    setSettingsMsg('');
    setSecureInvitePackage(null);
    try {
      const res = await authenticatedApi.post('/api/internal/auth-admin/invites/secure-send', {
        email: secureInviteEmail,
        role: secureInviteRole,
        ttl_hours: Number(secureInviteTtl),
        max_uses: Number(secureInviteMaxUses),
        one_time_delivery: true,
      });
      setSecureInvitePackage(res.data || null);
      await loadInternal();
    } catch (err) {
      setSettingsMsg(getAuthAwareErrorMessage(err, 'Failed to create secure invite package'));
    } finally {
      setSettingsLoading(false);
    }
  };

  return (
    <div className="signal-settings-overlay" data-testid="settings-overlay">
      <div className="signal-settings-panel">
        <div className="signal-settings-header">
          <div>
            <div className="signal-settings-title">Account Settings</div>
            <div className="signal-settings-sub">
              {user?.email} · <span className="signal-settings-role">{role}</span>
            </div>
          </div>
          <button className="signal-settings-close" onClick={onClose} data-testid="settings-close-btn">✕</button>
        </div>

        <div className="signal-settings-tabs">
          <button className={tab === 'account' ? 'active' : ''} onClick={() => setTab('account')} data-testid="settings-tab-account">Account</button>
          <button className={tab === 'password' ? 'active' : ''} onClick={() => setTab('password')} data-testid="settings-tab-password">Password</button>
          <button className={tab === 'email' ? 'active' : ''} onClick={() => setTab('email')} data-testid="settings-tab-email">Email</button>
          {internalTabs.map((entry) => (
            <button
              key={entry.key}
              className={tab === entry.key ? 'active' : ''}
              onClick={() => openInternalTab(entry.key)}
              data-testid={`settings-tab-${entry.key}`}
            >
              {entry.label}
            </button>
          ))}
        </div>

        <div className="signal-settings-body">
          {tab === 'account' && (
            <div className="signal-settings-section">
              <div className="signal-settings-row">
                <div className="signal-settings-key">Email</div>
                <div className="signal-settings-val">{user?.email}</div>
              </div>
              <div className="signal-settings-row">
                <div className="signal-settings-key">Email verified</div>
                <div className="signal-settings-val">
                  {user?.emailVerified ? (
                    <span className="signal-badge-ok">VERIFIED</span>
                  ) : (
                    <>
                      <span className="signal-badge-warn">UNVERIFIED</span>
                      <button className="signal-settings-btn-inline" onClick={sendVerify} data-testid="settings-send-verify-btn">
                        Send verification email
                      </button>
                    </>
                  )}
                </div>
              </div>
              <div className="signal-settings-row">
                <div className="signal-settings-key">Role</div>
                <div className="signal-settings-val"><span className="signal-settings-role">{role}</span></div>
              </div>
              <div className="signal-settings-row">
                <div className="signal-settings-key">UID</div>
                <div className="signal-settings-val signal-mono">{user?.uid}</div>
              </div>
              {verifyMsg.text && <div className={`signal-settings-msg ${verifyMsg.type}`}>{verifyMsg.text}</div>}

              <div className="signal-settings-danger">
                <button className="signal-settings-signout" onClick={logout} data-testid="settings-signout-btn">
                  SIGN OUT
                </button>
              </div>
            </div>
          )}

          {tab === 'password' && (
            <form className="signal-settings-section" onSubmit={submitPassword}>
              <div className="signal-settings-help">Enter your current password to change it.</div>
              <label className="signal-auth-label">
                <span>CURRENT PASSWORD</span>
                <input type="password" required value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} data-testid="settings-current-password" />
              </label>
              <label className="signal-auth-label">
                <span>NEW PASSWORD</span>
                <input type="password" required minLength={8} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} data-testid="settings-new-password" />
              </label>
              <label className="signal-auth-label">
                <span>CONFIRM NEW PASSWORD</span>
                <input type="password" required minLength={8} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} data-testid="settings-confirm-password" />
              </label>
              {pwMsg.text && <div className={`signal-settings-msg ${pwMsg.type}`}>{pwMsg.text}</div>}
              <button type="submit" className="signal-auth-submit" disabled={pwLoading} data-testid="settings-update-password-btn">
                {pwLoading ? 'UPDATING…' : 'UPDATE PASSWORD'}
              </button>
            </form>
          )}

          {tab === 'email' && (
            <form className="signal-settings-section" onSubmit={submitEmail}>
              <div className="signal-settings-help">Change your sign-in email. Requires current password.</div>
              <label className="signal-auth-label">
                <span>NEW EMAIL</span>
                <input type="email" required value={newEmail} onChange={(e) => setNewEmail(e.target.value)} data-testid="settings-new-email" />
              </label>
              <label className="signal-auth-label">
                <span>CURRENT PASSWORD</span>
                <input type="password" required value={emailCurrentPw} onChange={(e) => setEmailCurrentPw(e.target.value)} data-testid="settings-email-current-password" />
              </label>
              {emailMsg.text && <div className={`signal-settings-msg ${emailMsg.type}`}>{emailMsg.text}</div>}
              <button type="submit" className="signal-auth-submit" disabled={emailLoading} data-testid="settings-update-email-btn">
                {emailLoading ? 'UPDATING…' : 'UPDATE EMAIL'}
              </button>
            </form>
          )}

          {internalTabs.some((entry) => entry.key === tab) && (
            <div className="signal-settings-section">
              {!internalSettings && !settingsLoading && (
                <button className="signal-auth-submit" type="button" onClick={loadInternal}>Load Internal Settings</button>
              )}
              {settingsLoading && <div className="signal-settings-help">Loading internal control-plane settings…</div>}
              {settingsMsg && (
                <div className={`signal-settings-msg ${String(settingsMsg).toLowerCase().includes("failed") ? "err" : "ok"}`}>
                  {settingsMsg}
                </div>
              )}

              {tab === 'access-control' && (
                <>
                  <div className="signal-settings-help">Firebase role sync and signed invite packages are backend-only.</div>
                  <div className="signal-settings-row">
                    <div className="signal-settings-key">Allowlist</div>
                    <div className="signal-settings-val">{(internalSettings?.sections?.access_control?.admin_bootstrap_allowlist || []).join(', ') || '—'}</div>
                  </div>
                  <form className="signal-settings-section" onSubmit={createSecureInvitePackage}>
                    <label className="signal-auth-label">
                      <span>INVITE EMAIL</span>
                      <input type="email" value={secureInviteEmail} onChange={(e) => setSecureInviteEmail(e.target.value)} required />
                    </label>
                    <label className="signal-auth-label">
                      <span>ROLE</span>
                      <select value={secureInviteRole} onChange={(e) => setSecureInviteRole(e.target.value)}>
                        <option value="tech_support">tech_support</option>
                        <option value="admin">admin</option>
                      </select>
                    </label>
                    <label className="signal-auth-label">
                      <span>TTL HOURS</span>
                      <input type="number" min="1" max="720" value={secureInviteTtl} onChange={(e) => setSecureInviteTtl(e.target.value)} />
                    </label>
                    <label className="signal-auth-label">
                      <span>MAX USES</span>
                      <input type="number" min="1" max="20" value={secureInviteMaxUses} onChange={(e) => setSecureInviteMaxUses(e.target.value)} />
                    </label>
                    <button className="signal-auth-submit" type="submit" disabled={!isAdmin}>CREATE SECURE INVITE PACKAGE</button>
                  </form>
                  {secureInvitePackage && (
                    <div className="signal-settings-row">
                      <div className="signal-settings-key">Secure Package</div>
                      <div className="signal-settings-val signal-mono">{JSON.stringify(secureInvitePackage)}</div>
                    </div>
                  )}
                  <div className="signal-settings-help">Admins</div>
                  {(admins || []).map((adminRow) => (
                    <div className="signal-settings-row" key={adminRow.uid}>
                      <div className="signal-settings-key">{adminRow.email}</div>
                      <div className="signal-settings-val">
                        <span>{adminRow.role}</span>
                        <span>{adminRow.status}</span>
                        {isAdmin && (
                          <>
                            <button className="signal-settings-btn-inline" onClick={() => setAdminRole(adminRow.uid, adminRow.role === 'admin' ? 'tech_support' : 'admin')}>Toggle role</button>
                            <button className="signal-settings-btn-inline" onClick={() => setAdminStatus(adminRow.uid, adminRow.status !== 'disabled')}>{adminRow.status === 'disabled' ? 'Enable' : 'Disable'}</button>
                            <button className="signal-settings-btn-inline" onClick={() => forceReauth(adminRow.uid)}>Force re-auth</button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                  <div className="signal-settings-help">Recent auth audit</div>
                  {(auditRows || []).slice(0, 12).map((row, idx) => (
                    <div className="signal-settings-row" key={`${row.event}-${idx}`}>
                      <div className="signal-settings-key">{row.event}</div>
                      <div className="signal-settings-val">{row.timestamp || row.email || '—'}</div>
                    </div>
                  ))}
                </>
              )}

              {tab === 'time-location' && (
                <>
                  <label className="signal-auth-label">
                    <span>DEFAULT TIMEZONE</span>
                    <input value={timeDraft.default_timezone} onChange={(e) => setTimeDraft((prev) => ({ ...prev, default_timezone: e.target.value }))} />
                  </label>
                  <label className="signal-auth-label">
                    <span>DISPLAY LOCATION</span>
                    <input value={timeDraft.display_location} onChange={(e) => setTimeDraft((prev) => ({ ...prev, display_location: e.target.value }))} />
                  </label>
                  <label className="signal-auth-label">
                    <span>DISTRICT LOCAL TIME ENABLED</span>
                    <input type="checkbox" checked={timeDraft.district_local_time_enabled} onChange={(e) => setTimeDraft((prev) => ({ ...prev, district_local_time_enabled: e.target.checked }))} />
                  </label>
                  <button className="signal-auth-submit" type="button" onClick={saveTimeLocation} disabled={!isAdmin}>SAVE TIME / CALENDAR</button>
                </>
              )}

              {tab === 'calendar-district' && (
                <>
                  <label className="signal-auth-label">
                    <span>DISTRICT CALENDAR ENABLED</span>
                    <input type="checkbox" checked={calendarDraft.district_calendar_enabled} onChange={(e) => setCalendarDraft((prev) => ({ ...prev, district_calendar_enabled: e.target.checked }))} />
                  </label>
                  <label className="signal-auth-label">
                    <span>REFRESH MINUTES</span>
                    <input type="number" min="1" max="720" value={calendarDraft.calendar_refresh_minutes} onChange={(e) => setCalendarDraft((prev) => ({ ...prev, calendar_refresh_minutes: Number(e.target.value || 30) }))} />
                  </label>
                  <label className="signal-auth-label">
                    <span>OFFLINE CACHE HOURS</span>
                    <input type="number" min="1" max="168" value={calendarDraft.offline_cache_hours} onChange={(e) => setCalendarDraft((prev) => ({ ...prev, offline_cache_hours: Number(e.target.value || 48) }))} />
                  </label>
                </>
              )}

              {(tab === 'integrations' || tab === 'map-weather' || tab === 'bus-signing' || tab === 'runtime-show' || tab === 'sensor-controls') && (
                <pre className="signal-mono">
                  {JSON.stringify(
                    internalSettings?.sections?.[sectionKeyByTab[tab] || ''] || {},
                    null,
                    2
                  )}
                </pre>
              )}

              {tab === 'access-control' && (
                <div className="signal-settings-help">Invite states: active / used / expired / revoked</div>
              )}

              {tab === 'access-control' && (invites || []).slice(0, 12).map((row) => (
                <div className="signal-settings-row" key={row.jti || row.invite_id}>
                  <div className="signal-settings-key">{row.email}</div>
                  <div className="signal-settings-val">{row.role} · {row.status} · uses {row.use_count || 0}/{row.max_uses || 1}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
